import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import Swal from 'sweetalert2';
import { api } from '../../../api';
import RichTextEditor from 'components/RichTextEditor';
import MDDatePicker from 'components/MDDatePicker';

const GENERIC_FIELD_LABELS = [
  { key: 'patient_history', label: 'Personal History' },
  { key: 'family_history', label: 'Family History' },
  { key: 'allergic_to_drugs', label: 'Allergic to Drugs' },
  { key: 'drug_medical_history', label: 'Drug / Medical History' },
  { key: 'previous_infertility_treatment', label: 'Previous Infertility Treatment' },
  { key: 'notes', label: 'Notes' },
];

function MedicalHistoryDisplay({
  genericHistory,
  surgicalHistory,
  surgicalVisitOptions,
  selectedSurgicalVisit,
  onSurgicalVisitChange,
  onImportSurgicalHistory,
  importingSurgicalHistory,
  visitLabel,
  procedureHistories,
  procedureId,
  contactId,
  canEdit,
  canDelete,
  onSaved,
  surgicalHistoryOptions,
}) {
  const theme = useTheme();
  const primaryColor = theme.palette.logo?.main || '#00798E';
  const [editingHistory, setEditingHistory] = useState({});
  const [updatingHistory, setUpdatingHistory] = useState(false);
  const [procedureSurgicalHistories, setProcedureSurgicalHistories] = useState([]);

  // Extract procedure surgical histories filtered by contact ID
  useEffect(() => {
    if (!procedureHistories?.length || !contactId) return;
    const surgicalRecords = procedureHistories.filter(
      h => h.param_group_member?.slug === 'primary-history-common-params-surgical-history' &&
           h.irm_contacts_id === contactId
    );
    setProcedureSurgicalHistories(surgicalRecords);
  }, [procedureHistories, contactId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const handleEditHistory = (h) => {
    setEditingHistory(prev => ({
      ...prev,
      [h.id]: {
        value: h.value || '',
        history_date: h.history_date || '',
        remarks: h.remarks || '',
        param_group_member_option_id: h.irm_param_group_member_options_id || null,
      },
    }));
  };

  const handleCancelEdit = (id) => {
    setEditingHistory(prev => {
      const n = { ...prev };
      delete n[id];
      return n;
    });
  };

  const handleSaveEdit = async (h) => {
    const edited = editingHistory[h.id];
    if (!edited) return;
    setUpdatingHistory(true);
    try {
      // Find the selected option to get the value
      const selectedOption = surgicalHistoryOptions?.find(
        opt => opt.param_group_member_option_id === edited.param_group_member_option_id
      );
      
      const payload = {
        irm_procedures_id: procedureId,
        irm_contacts_id: contactId,
        irm_param_group_members_id: selectedOption?.param_group_member_id || h.irm_param_group_members_id,
        irm_param_group_member_options_id: edited.param_group_member_option_id || null,
        value: selectedOption?.value || edited.value,
        history_date: edited.history_date,
        remarks: edited.remarks,
      };
      await api.ivf.updateProcedureHistory(h.id, payload);
      
      // Update the local state with the new values including the option details
      setProcedureSurgicalHistories(prev =>
        prev.map(item => {
          if (item.id === h.id) {
            return {
              ...item,
              ...edited,
              value: selectedOption?.value || edited.value,
              irm_param_group_member_options_id: edited.param_group_member_option_id,
              param_group_member_option: selectedOption ? {
                value: selectedOption.value,
                option_value: selectedOption.label,
              } : item.param_group_member_option,
            };
          }
          return item;
        })
      );
      handleCancelEdit(h.id);
      Swal.fire({
        icon: 'success',
        title: 'Updated',
        text: 'Surgical record updated successfully.',
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
      if (onSaved) onSaved();
    } catch (err) {
      console.error('Error updating surgical history:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: err.message || 'Failed to update record.' });
    } finally {
      setUpdatingHistory(false);
    }
  };

  const handleDeleteHistory = async (h) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Delete Record',
      text: 'Are you sure you want to delete this surgical record?',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    try {
      await api.ivf.deleteProcedureHistory(h.id);
      setProcedureSurgicalHistories(prev => prev.filter(item => item.id !== h.id));
      Swal.fire({
        icon: 'success',
        title: 'Deleted',
        text: 'Surgical record deleted successfully.',
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
      if (onSaved) onSaved();
    } catch (err) {
      console.error('Error deleting surgical history:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: err.message || 'Failed to delete record.' });
    }
  };

  const filledFields = GENERIC_FIELD_LABELS.filter(({ key }) => {
    const v = genericHistory?.[key];
    return v && typeof v === 'string' && v.trim() !== '';
  });
  const hasGenericHistory = filledFields.length > 0;
  const hasSurgicalHistory = surgicalHistory && surgicalHistory.length > 0;
  const hasSurgicalImport = typeof onImportSurgicalHistory === 'function';
  const hasSurgicalVisitOptions = Array.isArray(surgicalVisitOptions) && surgicalVisitOptions.length > 0;

  if (!hasGenericHistory && !hasSurgicalHistory && !hasSurgicalImport) {
    return (
      <Box textAlign="center" py={3} sx={{ backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px dashed #dee2e6' }}>
        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>No previous medical or surgical history found</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {hasGenericHistory && (
        <Box mb={hasSurgicalHistory ? 2.5 : 0}>
          <Typography sx={{ fontWeight: 'bold', color: primaryColor, fontSize: '12px', mb: 1, borderBottom: '2px solid #00798E', paddingBottom: '4px' }}>
            Medical History
          </Typography>
          <TableContainer sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Table size="small">
              <TableBody>
                {filledFields.map(({ key, label }) => (
                  <TableRow key={key}>
                    <TableCell sx={{ fontWeight: 'bold', color: '#128296', width: '35%', fontSize: '10px', backgroundColor: '#f8f9fa', verticalAlign: 'top', p: 1.2 }}>
                      {label}
                    </TableCell>
                    <TableCell sx={{ fontSize: '10px', verticalAlign: 'top', p: 1.2, wordBreak: 'break-word', whiteSpace: 'normal' }}>
                      <Box
                        dangerouslySetInnerHTML={{ __html: genericHistory[key] }}
                        sx={{ '& p, & ul, & ol': { margin: 0, padding: 0 }, '& p': { marginBottom: 0 } }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {hasGenericHistory && (hasSurgicalHistory || hasSurgicalImport) && <Divider sx={{ my: 2, borderColor: '#e9ecef' }} />}

      {(hasSurgicalHistory || hasSurgicalImport || procedureSurgicalHistories.length > 0) && (
        <Box>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={1} flexWrap="wrap">
            <Typography sx={{ fontWeight: 'bold', color: primaryColor, fontSize: '12px', borderBottom: '2px solid #00798E', paddingBottom: '4px' }}>
              Surgical History
            </Typography>
            {hasSurgicalImport && (
              <Button
                size="small"
                variant="outlined"
                startIcon={importingSurgicalHistory ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <CloudDownloadIcon sx={{ fontSize: '14px !important' }} />}
                onClick={onImportSurgicalHistory}
                disabled={importingSurgicalHistory || !hasSurgicalVisitOptions}
                sx={{
                  borderColor: primaryColor,
                  color: primaryColor,
                  textTransform: 'none',
                  fontSize: '0.72rem',
                  py: 0.7,
                  px: 1.2,
                  whiteSpace: 'nowrap',
                  '&:hover': { backgroundColor: `${primaryColor}10` },
                }}
              >
                {importingSurgicalHistory ? 'Loading...' : 'Import Histories'}
              </Button>
            )}
          </Box>

          {procedureSurgicalHistories.length > 0 && (
            <Box mb={2}>
              <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                <Table size="small">
                  <TableBody>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '12px', width: '35%' }}>Surgery</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '12px', width: '15%' }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '12px', width: '35%' }}>Remarks</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '12px', textAlign: 'center', width: '15%' }}>Actions</TableCell>
                    </TableRow>
                    {procedureSurgicalHistories.map(h => {
                      const isEditing = !!editingHistory[h.id];
                      return (
                        <TableRow key={h.id} hover sx={{ backgroundColor: isEditing ? `${primaryColor}08` : 'inherit' }}>
                          <TableCell sx={{ fontSize: '12px', fontWeight: isEditing ? 600 : 400, color: isEditing ? primaryColor : 'inherit', textTransform: 'uppercase' }}>
                            {h.value || h.param_group_member_option?.value || h.param_group_member_option?.option_value || '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                            {formatDate(h.history_date)}
                          </TableCell>
                          <TableCell sx={{ fontSize: '12px', maxWidth: 160 }}>
                            <Box dangerouslySetInnerHTML={{ __html: h.remarks || '-' }} />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            {isEditing ? (
                              <Box display="flex" gap={0.5} justifyContent="center">
                                <IconButton
                                  size="small"
                                  onClick={() => handleSaveEdit(h)}
                                  disabled={updatingHistory}
                                  sx={{ color: '#28a745', '&:hover': { backgroundColor: 'rgba(40,167,69,0.1)' } }}
                                  title="Save"
                                >
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleCancelEdit(h.id)}
                                  disabled={updatingHistory}
                                  sx={{ color: '#6c757d', '&:hover': { backgroundColor: 'rgba(108,117,125,0.1)' } }}
                                  title="Cancel"
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            ) : (
                              <Box display="flex" gap={0.5} justifyContent="center">
                                {canEdit !== false && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditHistory(h)}
                                    sx={{ color: primaryColor, '&:hover': { backgroundColor: `${primaryColor}18` } }}
                                    title="Edit"
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                )}
                                {canDelete !== false && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteHistory(h)}
                                    sx={{ color: '#dc3545', '&:hover': { backgroundColor: 'rgba(220,53,69,0.1)' } }}
                                    title="Delete"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {Object.keys(editingHistory).length > 0 && (
                <Paper elevation={3} sx={{ mt: 1.5, p: 2.5, border: `2px solid ${primaryColor}`, borderRadius: 2, backgroundColor: '#f0fafb' }}>
                  {Object.keys(editingHistory).map(id => {
                    const h = procedureSurgicalHistories.find(item => item.id === parseInt(id));
                    if (!h) return null;
                    const edited = editingHistory[id];
                    return (
                      <Box key={id} mb={2}>
                        <Typography sx={{ fontWeight: 'bold', color: primaryColor, fontSize: '0.85rem', mb: 2, textTransform: 'uppercase' }}>
                          ✏️ Editing: {h.value || h.param_group_member_option?.value || h.param_group_member_option?.option_value || ''}
                        </Typography>
                        {surgicalHistoryOptions && surgicalHistoryOptions.length > 0 && (
                          <Box mb={2}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Surgery Type</InputLabel>
                              <Select
                                value={edited.param_group_member_option_id || ''}
                                label="Surgery Type"
                                onChange={(e) => {
                                  const selectedOption = surgicalHistoryOptions.find(
                                    opt => opt.param_group_member_option_id === e.target.value
                                  );
                                  setEditingHistory(prev => ({
                                    ...prev,
                                    [id]: {
                                      ...prev[id],
                                      param_group_member_option_id: e.target.value,
                                      value: selectedOption?.value || prev[id].value,
                                    },
                                  }));
                                }}
                                sx={{
                                  height: 40,
                                  fontSize: '0.875rem',
                                  '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                                }}
                              >
                                {surgicalHistoryOptions.map((option) => (
                                  <MenuItem key={option.param_group_member_option_id} value={option.param_group_member_option_id} sx={{ textTransform: 'uppercase' }}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                        )}
                        <Box mb={2}>
                          <MDDatePicker
                            label="Date"
                            value={edited.history_date || ''}
                            onChange={(date) =>
                              setEditingHistory(prev => ({
                                ...prev,
                                [id]: { ...prev[id], history_date: date },
                              }))
                            }
                            textFieldProps={{
                              fullWidth: true,
                              size: 'small',
                              sx: {
                                '& .MuiOutlinedInput-root': { height: 40, fontSize: '0.875rem' },
                                '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                              },
                            }}
                          />
                        </Box>
                        <RichTextEditor
                          label="Remarks"
                          value={edited.remarks || ''}
                          onChange={(val) =>
                            setEditingHistory(prev => ({
                              ...prev,
                              [id]: { ...prev[id], remarks: val },
                            }))
                          }
                          height="120px"
                        />
                      </Box>
                    );
                  })}
                </Paper>
              )}
            </Box>
          )}

          {hasSurgicalHistory && procedureSurgicalHistories.length === 0 && (
            <Box textAlign="center" py={3} sx={{ backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px dashed #dee2e6' }}>
              <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                No surgical history from old visits. Click &ldquo;Import Histories&rdquo; to load.
              </Typography>
            </Box>
          )}
          {!hasSurgicalHistory && procedureSurgicalHistories.length === 0 && (
            <Box textAlign="center" py={3} sx={{ backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px dashed #dee2e6' }}>
              <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                No surgical history found.
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

MedicalHistoryDisplay.propTypes = {
  genericHistory: PropTypes.object,
  importingSurgicalHistory: PropTypes.bool,
  onImportSurgicalHistory: PropTypes.func,
  onSurgicalVisitChange: PropTypes.func,
  selectedSurgicalVisit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  surgicalHistory: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      surgery: PropTypes.string,
      value: PropTypes.string,
      notes: PropTypes.string,
      remarks: PropTypes.string,
      history_date: PropTypes.string,
      visit_no: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  surgicalVisitOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      visit_no: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  visitLabel: PropTypes.string,
  procedureHistories: PropTypes.array,
  procedureId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  contactId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  canEdit: PropTypes.bool,
  canDelete: PropTypes.bool,
  onSaved: PropTypes.func,
  surgicalHistoryOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
      param_group_member_id: PropTypes.number,
      param_group_member_option_id: PropTypes.number,
      param_group_id: PropTypes.number,
    })
  ),
};

MedicalHistoryDisplay.defaultProps = {
  genericHistory: null,
  importingSurgicalHistory: false,
  onImportSurgicalHistory: null,
  onSurgicalVisitChange: null,
  selectedSurgicalVisit: 'ALL_VISITS',
  surgicalHistory: [],
  procedureHistories: [],
  procedureId: null,
  contactId: null,
  canEdit: true,
  canDelete: true,
  onSaved: null,
  surgicalVisitOptions: [],
  visitLabel: 'Visit',
  surgicalHistoryOptions: [],
};

export default MedicalHistoryDisplay;
